import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import { lazy, Suspense } from "react";
import CookieBanner from "./components/CookieBanner";
import WhatsAppButton from "./components/WhatsAppButton";
import HolidayBanner from "./components/HolidayBanner";
import ScrollToTop from "./components/ScrollToTop";
import Seo from "./components/Seo";
import LanguageUrlSync from "./components/LanguageUrlSync";
import LocalizedRoute from "./components/LocalizedRoute";

const HomePage = lazy(() => import("./pages/HomePage"));
const MenuPage = lazy(() => import("./pages/MenuPage"));
const ReservationPage = lazy(() => import("./pages/ReservationPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const TakeawayPage = lazy(() => import("./pages/TakeawayPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LanguageRedirect = lazy(() => import("./components/LanguageRedirect"));

const Loader = () => (
  <div className="min-h-screen bg-[var(--ink)] flex items-center justify-center text-[var(--gold)]" style={{ fontFamily: "var(--han)", fontSize: "2rem" }}>漢宮酒樓</div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  const localizedPages = [
    ["", <HomePage />], ["/menu", <MenuPage />], ["/reservation", <ReservationPage />],
    ["/takeaway", <TakeawayPage />], ["/gallery", <GalleryPage />], ["/contact", <ContactPage />],
    ["/impressum", <ImpressumPage />], ["/privacy", <PrivacyPage />],
  ];
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Seo />
          <LanguageUrlSync />
          <a href="#page-content" className="skip-link">Zum Inhalt springen</a>
          <Toaster position="top-right" toastOptions={{ style: { background: 'var(--ink)', border: '1px solid rgba(212,161,74,0.3)', color: 'var(--paper)' } }} />
          <ScrollToTop />
          <HolidayBanner />
          <CookieBanner />
          <WhatsAppButton />
          <main id="page-content" tabIndex="-1">
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/menu" element={<MenuPage />} />
              <Route path="/reservation" element={<ReservationPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/takeaway" element={<TakeawayPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/impressum" element={<ImpressumPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {(["de", "en", "fr", "it", "zh"]).flatMap(code =>
                localizedPages.map(([path, page]) => (
                  <Route key={`${code}${path || "/"}`} path={`/${code}${path}`} element={<LocalizedRoute lang={code}>{page}</LocalizedRoute>} />
                ))
              )}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              {/* Legacy language-prefix URLs from Google My Business / old CMS */}
              <Route path="/de/*" element={<LanguageRedirect lang="de" />} />
              <Route path="/en/*" element={<LanguageRedirect lang="en" />} />
              <Route path="/fr/*" element={<LanguageRedirect lang="fr" />} />
              <Route path="/it/*" element={<LanguageRedirect lang="it" />} />
              <Route path="/zh/*" element={<LanguageRedirect lang="zh" />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          </main>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
